import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Container, Box, Typography, Paper, Grid, Tabs, Tab, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Avatar, Chip, Button,
    CircularProgress, useTheme, useMediaQuery, Divider, Tooltip, IconButton,
} from '@mui/material';
import {
    Speed as SpeedIcon, TrendingUp as TrendingIcon, LocalFireDepartment as FireIcon,
    MenuBook as BookIcon, EmojiEvents as TrophyIcon, Bolt as BoltIcon,
    Share as ShareIcon, Person as PersonIcon, Psychology as PsychIcon,
} from '@mui/icons-material';
import { flexLeaderboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { trackFlexEvent, FLEX_EVENTS } from '../utils/flexAnalytics';

const SORT_OPTIONS = [
    { value: 'effectiveWpm', label: 'Effective WPM', icon: BoltIcon, color: '#FFD700', desc: 'WPM × comprehension (80%+ required)' },
    { value: 'wpm', label: 'Top Speed', icon: SpeedIcon, color: '#00BFFF', desc: 'Highest single-session WPM' },
    { value: 'comprehension', label: 'Comprehension', icon: PsychIcon, color: '#8DC63F', desc: 'Best understanding scores' },
    { value: 'streak', label: 'Streaks', icon: FireIcon, color: '#FF9800', desc: 'Current consecutive days' },
    { value: 'mostImproved', label: 'Most Improved', icon: TrendingIcon, color: '#9C27B0', desc: 'Biggest monthly gain' },
    { value: 'volume', label: 'Volume', icon: BookIcon, color: '#2196F3', desc: 'Total words read' },
    { value: 'challenges', label: 'Challenge Wins', icon: TrophyIcon, color: '#FFD700', desc: 'Head-to-head victories' },
];

const FlexLeaderboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { handle } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [sort, setSort] = useState('effectiveWpm');
    const [period, setPeriod] = useState('allTime');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [myRank, setMyRank] = useState(null);

    const fetchLeaderboard = useCallback(async () => {
        try {
            setLoading(true);
            const result = await flexLeaderboardAPI.getLeaderboard(sort, period, 50);
            setData(result);
            trackFlexEvent(FLEX_EVENTS.LEADERBOARD_VIEWED, { sort, period });
        } catch (err) {
            console.error('Failed to fetch leaderboard:', err);
        } finally {
            setLoading(false);
        }
    }, [sort, period]);

    const fetchMyRank = useCallback(async () => {
        if (!user) return;
        try {
            const result = await flexLeaderboardAPI.getMyRank();
            setMyRank(result);
        } catch (err) {
            console.error('Failed to fetch my rank:', err);
        }
    }, [user]);

    useEffect(() => {
        fetchLeaderboard();
    }, [fetchLeaderboard]);

    useEffect(() => {
        fetchMyRank();
    }, [fetchMyRank]);

    const currentSort = SORT_OPTIONS.find(s => s.value === sort);

    const handleRowClick = (rowHandle) => {
        navigate(`/flex/${rowHandle}`);
    };

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
    };

    return (
        <>
            <Helmet>
                <title>FLEX Leaderboards | ReaderFLEX</title>
                <meta name="description" content="Compete on ReaderFLEX leaderboards — Effective WPM, comprehension, streaks, volume, and challenge wins." />
            </Helmet>

            <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <TrophyIcon sx={{ color: '#FFD700', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        FLEX Leaderboards
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {currentSort?.desc}. Only users who opt in to public leaderboards appear here.
                </Typography>

                {/* My Rank Summary */}
                {myRank?.participating && (
                    <Paper elevation={0} sx={{
                        p: { xs: 2, sm: 3 }, borderRadius: 2,
                        border: `1px solid ${theme.palette.primary.main}`,
                        mb: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <PersonIcon color="primary" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Your Rankings
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {Object.entries(myRank.ranks).map(([key, rank]) => {
                                const opt = SORT_OPTIONS.find(s => s.value === key);
                                if (!opt || !rank) return null;
                                return (
                                    <Chip
                                        key={key}
                                        icon={<opt.icon sx={{ color: opt.color }} />}
                                        label={`#${rank} ${opt.label}`}
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                );
                            })}
                        </Box>
                    </Paper>
                )}

                {myRank && !myRank.participating && user && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            You're not participating in leaderboards. Enable it in{' '}
                            <Button size="small" href="/profile" sx={{ textTransform: 'none' }}>Profile Settings → FLEX Profile</Button>.
                        </Typography>
                    </Paper>
                )}

                {/* Sort Tabs */}
                <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                    <Tabs
                        value={sort}
                        onChange={(_, v) => setSort(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ minHeight: 48 }}
                    >
                        {SORT_OPTIONS.map(opt => (
                            <Tab
                                key={opt.value}
                                value={opt.value}
                                icon={<opt.icon sx={{ color: opt.color, fontSize: 18 }} />}
                                iconPosition="start"
                                label={isMobile ? '' : opt.label}
                                sx={{ minHeight: 48, minWidth: { xs: 48, sm: 120 } }}
                            />
                        ))}
                    </Tabs>
                </Paper>

                {/* Period Toggle */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <Button
                        size="small"
                        variant={period === 'allTime' ? 'contained' : 'outlined'}
                        onClick={() => setPeriod('allTime')}
                    >
                        All Time
                    </Button>
                    <Button
                        size="small"
                        variant={period === 'monthly' ? 'contained' : 'outlined'}
                        onClick={() => setPeriod('monthly')}
                    >
                        This Month
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Share leaderboard">
                        <IconButton size="small" onClick={handleShare}>
                            <ShareIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Leaderboard Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : !data || data.leaderboard.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="body1" color="text.secondary">
                            No participants yet for this category.
                        </Typography>
                        {user && (
                            <Button variant="outlined" href="/reader" sx={{ mt: 2 }}>
                                Start Reading to Compete
                            </Button>
                        )}
                    </Paper>
                ) : (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            {data.totalParticipants} participants
                            {sort === 'effectiveWpm' && ` · ${data.minComprehension}% comprehension required`}
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                                        <TableCell sx={{ fontWeight: 700, width: 50 }}>Rank</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Reader</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {sort === 'effectiveWpm' && 'Eff. WPM'}
                                            {sort === 'wpm' && 'Best WPM'}
                                            {sort === 'comprehension' && 'Comprehension'}
                                            {sort === 'streak' && 'Streak'}
                                            {sort === 'mostImproved' && 'Improvement'}
                                            {sort === 'volume' && 'Words Read'}
                                            {sort === 'challenges' && 'Wins'}
                                        </TableCell>
                                        {!isMobile && (
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Level</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.leaderboard.map((row) => {
                                        const isMe = user && row.handle === user.profile?.displayName;
                                        const medal = row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : null;
                                        return (
                                            <TableRow
                                                key={row._id}
                                                hover
                                                onClick={() => handleRowClick(row.handle)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    bgcolor: isMe ? alpha(theme.palette.primary.main, 0.06) : 'inherit',
                                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                                }}
                                            >
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    {medal || `#${row.rank}`}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar
                                                            src={row.avatarUrl}
                                                            sx={{ width: 32, height: 32 }}
                                                        >
                                                            {row.handle[0]?.toUpperCase()}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                @{row.handle}
                                                                {isMe && <Chip label="You" size="small" color="primary" sx={{ ml: 1, height: 18 }} />}
                                                            </Typography>
                                                            {!isMobile && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {row.title}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                    {sort === 'effectiveWpm' && row.effectiveWpm}
                                                    {sort === 'wpm' && row.bestWPM}
                                                    {sort === 'comprehension' && `${row.comprehensionRate}%`}
                                                    {sort === 'streak' && `${row.currentStreak}d`}
                                                    {sort === 'mostImproved' && `+${row.improvement}`}
                                                    {sort === 'volume' && row.totalWordsRead.toLocaleString()}
                                                    {sort === 'challenges' && row.flexStats.challengesWon}
                                                </TableCell>
                                                {!isMobile && (
                                                    <TableCell align="right">
                                                        <Chip label={`Lv ${row.level}`} size="small" variant="outlined" />
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Top 3 Podium for mobile */}
                        {isMobile && data.leaderboard.length >= 3 && (
                            <Grid container spacing={1} sx={{ mt: 2 }}>
                                {data.leaderboard.slice(0, 3).map((row, i) => (
                                    <Grid item xs={4} key={row._id}>
                                        <Paper
                                            elevation={0}
                                            sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
                                            onClick={() => handleRowClick(row.handle)}
                                        >
                                            <Typography variant="h6">{['🥇', '🥈', '🥉'][i]}</Typography>
                                            <Avatar src={row.avatarUrl} sx={{ width: 36, height: 36, mx: 'auto', my: 0.5 }}>
                                                {row.handle[0]?.toUpperCase()}
                                            </Avatar>
                                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }} noWrap>
                                                @{row.handle}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                )}

                {!user && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Want to compete? Join ReaderFLEX and start reading.
                        </Typography>
                        <Button variant="contained" size="large" href="/register">
                            Get Started
                        </Button>
                    </Box>
                )}
            </Container>
        </>
    );
};

function alpha(color, value) {
    return require('@mui/material/styles').alpha(color, value);
}

export default FlexLeaderboard;
